import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { HatLocationPipe } from '../../shared/hat-location.pipe';
import { Organization, OrgSecurityGroup, OrgHat } from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { NormalizePipe } from '../../format-pipe';
import { CommonModule } from '@angular/common';
import { ProfileColorService } from '../../services/profile-color.service';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgBoardService } from '../../services/org-board-service';
import { Subject, takeUntil } from 'rxjs';

// Organization features that trigger system route access (any of these grants access to default org pages)
const ORG_SYSTEM_FEATURES = [
    'organization',
    'organization-dashboard',
    'organization-settings',
    'organization-integrations',
    'organization-targets',
    'organization-dates',
];

// Management tools features that trigger system route access (any of these grants access to dashboard)
const MGMT_SYSTEM_FEATURES = [
    'management-tools',
    'management-tools-dashboard',
    'statistics',
    'checklist',
    'client-abc',
];

// Menu configuration for preview (mirrors the app's sidebar menu structure)
const MENU_ITEMS: { feature: string; name: string; icon: string; section: string }[] = [
    // Home section (top-level navigation)
    { feature: 'workspace', name: 'WorkSpace', icon: 'account_balance_wallet', section: 'Home' },
    { feature: 'management-tools-dashboard', name: 'Management Tools', icon: 'engineering', section: 'Home' },
    { feature: 'organization', name: 'Organization', icon: 'corporate_fare', section: 'Home' },
    // Workspace section
    { feature: 'workspace', name: 'Inbasket', icon: 'inbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Pending', icon: 'pending', section: 'Workspace' },
    { feature: 'workspace', name: 'Outbasket', icon: 'outbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Filed', icon: 'task_alt', section: 'Workspace' },
    { feature: 'workspace', name: 'Search', icon: 'search', section: 'Workspace' },
    // Management Tools
    { feature: 'management-tools-dashboard', name: 'Dashboard', icon: 'dashboard', section: 'Management Tools' },
    { feature: 'checklist', name: 'Checklist', icon: 'checklist', section: 'Management Tools' },
    { feature: 'statistics', name: 'Statistics', icon: 'analytics', section: 'Management Tools' },
    { feature: 'client-abc', name: 'Client ABC', icon: 'hotel_class', section: 'Management Tools' },
    // Organization (system routes - accessible with any org feature via system route inheritance)
    { feature: 'organization', name: 'Dashboard', icon: 'dashboard', section: 'Organization' },
    { feature: 'organization', name: 'My Hats', icon: 'school', section: 'Organization' },
    { feature: 'organization', name: 'My Policies', icon: 'fact_check', section: 'Organization' },
    { feature: 'organization', name: 'Org Board', icon: 'account_tree', section: 'Organization' },
    { feature: 'organization-targets', name: 'Targets', icon: 'multiline_chart', section: 'Organization' },
    { feature: 'organization-dates', name: 'Date Cycles', icon: 'event', section: 'Organization' },
    {
        feature: 'organization-integrations',
        name: 'Integrations',
        icon: 'integration_instructions',
        section: 'Organization',
    },
    { feature: 'organization', name: 'Manage', icon: 'settings_applications', section: 'Organization' },
    { feature: 'organization-settings', name: 'Settings', icon: 'settings', section: 'Organization' },
];

export interface SecurityGroupDetailsDialogData {
    securityGroup: OrgSecurityGroup;
    securityGroupKey: string;
    organization: Organization;
}

interface HatUsingGroup {
    key: string;
    name: string;
    userCount: number;
}

interface UserViaHat {
    key: string;
    name: string;
    hatName: string;
}

interface FeatureDisplay {
    key: string;
    name: string;
    description?: string;
    permissionDescriptions?: Record<string, string>;
    enabled: boolean;
    permissions: string[];
}

@Component({
    selector: 'shared-org-security-group-details-dialog',
    imports: [HatLocationPipe, MaterialModule, NormalizePipe, CommonModule],
    templateUrl: './org-security-group-details-dialog.html',
    styleUrls: [
        '../org-dialog.scss',
        '../../organization-board.component.scss',
        './org-security-group-details-dialog.scss',
    ],
})
export class OrgSecurityGroupDetailsDialogComponent implements OnInit, OnDestroy {
    public securityGroup: OrgSecurityGroup | null = null;
    public hatsUsingGroup: HatUsingGroup[] = [];
    public usersViaHats: UserViaHat[] = [];
    public features: FeatureDisplay[] = [];
    public policies: string[] = [];
    public menuPreview: { section: string; items: { name: string; icon: string }[] }[] = [];

    // Section collapse states
    public sectionStates = {
        hats: true,
        users: true,
        features: true,
        policies: true,
        menuPreview: true,
    };

    private profileColorService = inject(ProfileColorService);
    public orgDialogService = inject(OrgDialogService);
    private obService = inject(OrgBoardService);
    private destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<OrgSecurityGroupDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SecurityGroupDetailsDialogData,
        private dialog: MatDialog
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-153 item 4) — the class
        // is phone-scoped globally, desktop unaffected.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    public ngOnInit(): void {
        console.log('OrgSecurityGroupDetailsDialogComponent > ngOnInit > data:', this.data);
        this.securityGroup = this.data.securityGroup;
        this.loadSecurityGroupDetails();
        this.subscribeToOrgUpdates();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            const sgKey = this.data.securityGroupKey;
            if (sgKey && org.orgSecurityGroups?.[sgKey]) {
                this.securityGroup = org.orgSecurityGroups[sgKey] as OrgSecurityGroup;
                this.data.securityGroup = this.securityGroup;
            }
            this.loadSecurityGroupDetails();
        });
    }

    private loadSecurityGroupDetails(): void {
        if (!this.securityGroup) {
            console.warn('No security group data provided');
            return;
        }

        const org = this.data.organization;

        // Load hats using this security group
        this.loadHatsUsingGroup(org);

        // Load users via hats
        this.loadUsersViaHats(org);

        // Load features and permissions
        this.loadFeatures();

        // Load policies
        this.loadPolicies();

        // Compute menu preview
        this.computeMenuPreview();
    }

    private loadHatsUsingGroup(org: Organization): void {
        const sgKey = this.data.securityGroupKey;
        const orgHats = org?.orgHats;

        if (!orgHats?.hatsKeys) {
            this.hatsUsingGroup = [];
            return;
        }

        this.hatsUsingGroup = [];

        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey];
            if (hat && typeof hat === 'object' && 'securityGroupsArray' in hat) {
                const hatObj = hat as OrgHat;
                const hatSgArray = hatObj.securityGroupsArray || [];

                // Check if this hat uses our security group
                const usesSg = hatSgArray.some((sg: string | { key?: string }) => {
                    const sgKeyFromArray = typeof sg === 'string' ? sg : sg?.key;
                    return sgKeyFromArray === sgKey;
                });

                if (usesSg) {
                    const userCount = hatObj.assignedToArray?.length || hatObj.userKeys?.length || 0;
                    this.hatsUsingGroup.push({
                        key: hatKey,
                        name: hatObj.name || hatKey,
                        userCount,
                    });
                }
            }
        }

        // Sort by name
        this.hatsUsingGroup.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadHatsUsingGroup > hatsUsingGroup:', this.hatsUsingGroup);
    }

    private loadUsersViaHats(org: Organization): void {
        const orgUsers = org?.orgUsers;

        if (!orgUsers) {
            this.usersViaHats = [];
            return;
        }

        const usersMap = new Map<string, UserViaHat>();

        for (const hatInfo of this.hatsUsingGroup) {
            const hat = org.orgHats?.[hatInfo.key] as OrgHat | undefined;
            if (hat) {
                const userKeys = hat.assignedToArray || hat.userKeys || [];

                for (const userKey of userKeys) {
                    if (!usersMap.has(userKey)) {
                        const orgUser = orgUsers[userKey];
                        const userName =
                            orgUser && typeof orgUser === 'object'
                                ? (orgUser as { name?: string; userName?: string }).name ||
                                  (orgUser as { name?: string; userName?: string }).userName ||
                                  userKey
                                : userKey;

                        usersMap.set(userKey, {
                            key: userKey,
                            name: userName,
                            hatName: hatInfo.name,
                        });
                    }
                }
            }
        }

        this.usersViaHats = Array.from(usersMap.values());
        this.usersViaHats.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadUsersViaHats > usersViaHats:', this.usersViaHats);

        // Ensure profile colors are loaded
        this.profileColorService.ensureLoadedMany(this.usersViaHats.map((u) => u.key));
    }

    private loadFeatures(): void {
        const featureDetails = this.securityGroup?.featureDetails || {};
        const featureKeys = this.securityGroup?.features || [];

        // Get org features for name lookup
        const orgFeatures = this.data.organization?.orgFeatures || {};

        this.features = featureKeys.map((featureKey: string) => {
            const detail = featureDetails[featureKey];
            const orgFeature = orgFeatures[featureKey];

            // Get feature name from detail, org feature, or fallback to key
            let featureName = featureKey;
            if (detail && typeof detail === 'object' && 'name' in detail) {
                featureName = detail.name || featureKey;
            } else if (orgFeature && typeof orgFeature === 'object' && 'name' in orgFeature) {
                featureName = (orgFeature as { name?: string }).name || featureKey;
            }

            return {
                key: featureKey,
                name: featureName,
                description: (orgFeature as any)?.description || '',
                permissionDescriptions: (orgFeature as any)?.permissionDescriptions || {},
                enabled: detail?.enabled ?? true,
                permissions: detail?.permissions || [],
            };
        });

        // Sort by name
        this.features.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadFeatures > features:', this.features);
    }

    private loadPolicies(): void {
        this.policies = this.securityGroup?.policies || [];
        console.log('loadPolicies > policies:', this.policies);
    }

    /**
     * Compute which menu items users in this security group would see,
     * based on the features assigned to this security group.
     */
    private computeMenuPreview(): void {
        const allFeatures = new Set<string>();

        // Collect features from this security group
        const featureKeys = this.securityGroup?.features || [];
        for (const f of featureKeys) {
            allFeatures.add(f);
        }

        // Also check featureDetails keys as fallback
        const featureDetails = this.securityGroup?.featureDetails || {};
        for (const fKey of Object.keys(featureDetails)) {
            allFeatures.add(fKey);
        }

        // Handle system route inheritance: if ANY org feature is present, add 'organization' parent
        // This mirrors the system route behavior where My Hats, My Policies, Org Board, Dashboard, and Manage
        // are accessible with any organization-* feature
        if (ORG_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('organization');
        }

        // Handle management tools system route: if ANY mgmt feature is present, add 'management-tools-dashboard'
        // This mirrors the system route behavior where Dashboard is accessible with any mgmt feature
        if (MGMT_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('management-tools-dashboard');
        }

        // Filter menu items by available features
        const accessibleItems = MENU_ITEMS.filter((item) => allFeatures.has(item.feature));

        // Group by section (deduplicate by name)
        const grouped = new Map<string, { name: string; icon: string }[]>();
        for (const item of accessibleItems) {
            if (!grouped.has(item.section)) {
                grouped.set(item.section, []);
            }
            const existing = grouped.get(item.section) || [];
            if (!existing.some((e) => e.name === item.name)) {
                existing.push({ name: item.name, icon: item.icon });
            }
        }

        // Convert to array with proper section ordering
        const sectionOrder = ['Home', 'Workspace', 'Management Tools', 'Organization'];
        this.menuPreview = sectionOrder
            .filter((section) => grouped.has(section))
            .map((section) => ({
                section,
                items: grouped.get(section) || [],
            }));
    }

    public getTotalPermissionsCount(): number {
        return this.features.reduce((total, feature) => total + feature.permissions.length, 0);
    }

    public getInitials(name: string): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     */
    public getUserAvatarUrl(user: UserViaHat): string {
        const org = this.data.organization;

        // Check organization's orgUsers for photoThumbnail
        if (org?.orgUsers?.[user.key]?.photoThumbnail) {
            return org.orgUsers[user.key].photoThumbnail!;
        }

        // Fall back to generated avatar
        const name = user.name || 'User';
        return this.profileColorService.getAvatarUrl(user.key, name, 40);
    }

    /**
     * Open the edit security group dialog
     */
    public openEditDialog(): void {
        this.dialogRef.close();
        import('../org-security-group/org-security-group').then((m) => {
            this.dialog.open(m.OrgSecurityGroupComponent, {
                data: {
                    organization: this.data.organization,
                    goToNewTab: false,
                },
                height: '85%',
                width: '65%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            });
        });
    }
}
